/**
 * @fileoverview One form field row: label, control and validation-error bubble.
 * Consumed by `LayoutUI` and by anything else that renders a `UIField`.
 *
 * This component is on the keystroke path — every character typed re-renders it,
 * so the decorative branches are split out and memoized (`FieldLabel`,
 * `FieldErrorTooltip`) and every prop handed to the memoized `ControlUI` is
 * referentially stable: the handlers are `useCallback`, the style literal is
 * module-level. Adding an inline object or arrow here silently un-memoizes the
 * control for focus, blur and protection changes.
 *
 * The wrapper carries `data-pdf-field-group`, which the PDF export stylesheet
 * turns into `break-inside: avoid`. It must stay on field-sized subtrees only —
 * putting it on a container that can outgrow a page (an ArTable) forces that
 * whole block onto a fresh sheet (SYD-2298).
 *
 * A Form Derived Value target is locked here and explained by `FieldLabel`'s `fx`
 * mark; `useFormDeriveFieldState` supplies the filler-facing facts and
 * `useFieldUI`'s `externalValue` seam supplies the live copy-a-field value. The
 * disable itself rides `effectiveDisabled` into `useFieldUI`, so the field-type
 * default cannot be auto-applied under a derived value; the control renders from
 * the `disabled` that hook hands back, which is `effectiveDisabled` widened by
 * the composite sub-fields a formula or a projection computes. That widening
 * carries its own sentence — `computedSubFieldReason` — onto the control as a
 * native `title`, the way a projected cell explains itself in the table. An antd
 * `Tooltip` cannot serve: it clones the child to inject its handlers, and every
 * memoized control below drops them.
 *
 * While a derive computes, the shared `calculating-value` class goes on the control
 * — the value pulses, the `fx` mark stays still — and `ControlUI` forwards it to
 * every antd control through `commonProps`, except on its `composite` branch, which
 * renders `CompositeField` without them and therefore never pulses.
 *
 * Right-click Copy Field / Paste Field belongs to the form's own `FormContextMenu`,
 * which resolves the field under the pointer from the `data-form-field-key` stamped
 * on the wrapper below and the matching `formFieldClipboard` registration. A string
 * attribute and an effect keep the memo contract above; a paste writes through
 * `setValue`, the same path a keystroke takes, so validation, persistence and undo
 * are the ones the field already has (SYD-2232). Every field stamps the key,
 * including one that refuses writes: that registration carries a `null` writer, so
 * Copy Field still reaches a derived or protected value and only Paste goes dark.
 *
 * `recovery-differing-value` rides the same seam and with the same exception: it
 * tints a field a point-in-time restore of the previewed moment would change
 * (SYD-2450). Both are class names rather than styles precisely because of the
 * memo contract above. The VALUE such a field shows during that preview is the
 * retained one, not the live one — `useFormRecoveryFieldState` carries it and
 * `useFieldUI` layers it over `externalValue`, so it arrives here as the ordinary
 * `value` and every field type gets it from one seam.
 */

import { FC, useState, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import { UIField } from '#types/System.types';
import type { ArCellValue } from '#types/System.Artifacts.types';
import FlexCol from '../atoms/FlexCol';
import FieldLabel from '../atoms/FieldLabel';
import ControlUI from './ControlUI';
import FieldErrorTooltip from './FieldErrorTooltip';
import { formFieldClipboard, type FieldClipboardTarget } from '../../store/form';
import { useFieldUI } from '../../use/useFieldUI';
import { useProtectedFields } from '../../use/useProtectedFields';
import { useFormDeriveFieldState } from '../../use/useFormDeriveFieldState';
import { useFormRecoveryFieldState } from '../../use/form/useFormRecoveryFieldState';
import { formDeriveCopy } from '@/services/formDerives';

const controlStyle = { width: '100%' };
/**
 * Spec §10: a derived value the engine could not produce shows an empty control
 * with italic placeholder text and NEVER a zero — a zero where the answer is
 * unknown is the harm the engine's strict coercion exists to prevent. Italic is
 * inherited by the control's own `::placeholder`, so this covers every field type.
 * Module-level, because an inline literal would un-memoize `ControlUI`.
 */
const noValueControlStyle = { width: '100%', fontStyle: 'italic' };

/**
 * Props for the FieldUI component
 */
interface FieldUIProps {
  /** Configuration object containing field properties and metadata */
  uiField: UIField;
  /** Whether the field should be in read-only/disabled state */
  disabled?: boolean;
}

/**
 * A form field component that handles rendering, validation, and data updates.
 * 
 * @component
 * @memo
 * 
 * @features
 * - Real-time validation with visual feedback
 * - Context-aware data persistence (Entity or Form context)
 * - Tooltip-based help text and error messages
 * - Read-only mode support
 * - Automatic field type detection and rendering
 * - Debounced updates to prevent excessive writes
 * 
 * @context
 * - Requires either EntityContext or FormContext
 * - Also uses UserContext for event logging
 * 
 * @throws {Alert}
 * Renders an error Alert if neither EntityContext nor FormContext is available
 */
const FieldUI: FC<FieldUIProps> = memo(({
  uiField,
  disabled = false
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const { isFieldProtected, isFieldDerived } = useProtectedFields();
  const isProtected = isFieldProtected(uiField.fieldId);
  /**
   * A Form Derived Value target is server-owned: the reconciler writes it and
   * `firestore.rules` denies the client. Disabled, never hidden — the user must
   * still be able to read the value the derive produced.
   */
  const isDerived = isFieldDerived(uiField.fieldId);
  const effectiveDisabled = disabled || isProtected || isDerived;
  /**
   * Everything the filler is told about the derive. Absent — every member unset —
   * while this version's `formDerives.rev` lags the FormType's `formDeriveRev`:
   * the lock still holds and the `fx` mark still explains it, but a version that
   * has not caught up claims nothing about what the value is or when it ran.
   */
  const derive = useFormDeriveFieldState(uiField.fieldId);
  /**
   * Whether a point-in-time restore of the previewed moment would change this
   * field. False whenever no preview is running, so the tint appears and
   * disappears with the preview and nothing else. Only `differs` is read here;
   * the retained value half of the same state is consumed by `useFieldUI`.
   */
  const recovery = useFormRecoveryFieldState(uiField.fieldId);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setShowPopover(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    setShowPopover(false);
  }, []);

  const handleDismissError = useCallback(() => {
    setShowPopover(false);
  }, []);

  /**
   * `controlDisabled` is `effectiveDisabled` widened by the composite context's
   * computed sub-fields — a formula target, or one a Form Derive projection fills.
   * `useFieldUI` refuses those writes already, so the control renders from what the
   * hook hands back rather than the narrower prop, and a sub-field the platform
   * owns looks disabled instead of merely inert. `writeRefused` widens it once more
   * with the layout editor's preview surface and answers a different question: not
   * how the control renders, but whether a write lands at all.
   */
  const {
    hasContext,
    value,
    setValue,
    disabled: controlDisabled,
    writeRefused,
    computedSubFieldReason,
    validationStatus,
    props,
    errorMessage,
    label,
    description,
    fieldType,
    fieldTypesLoading,
    clipboardFieldKey,
  } = useFieldUI<ArCellValue>({
    uiField,
    disabled: effectiveDisabled,
  });

  /**
   * What the field shows right now, read at click time by the form's menu. A ref
   * rather than the value itself, so the registered target keeps its identity
   * through every keystroke and the registration effect runs once per field.
   */
  const shownValueRef = useRef(value);
  shownValueRef.current = value;

  /**
   * The key answers which field this is and `write` answers whether it takes a
   * value, so a derived, protected or previewed field registers copy-only rather
   * than not at all — Copy Field is exactly what a person wants on those.
   */
  const clipboardTarget = useMemo<FieldClipboardTarget>(() => ({
    read: () => shownValueRef.current,
    fieldId: uiField.fieldId,
    fieldType,
    write: writeRefused ? null : setValue,
  }), [uiField.fieldId, fieldType, writeRefused, setValue]);

  useEffect(() => {
    if (!clipboardFieldKey) return;
    return formFieldClipboard.registerTarget(clipboardFieldKey, clipboardTarget);
  }, [clipboardFieldKey, clipboardTarget]);

  if (!hasContext && value === null) {
    return null;
  }

  if (!fieldType && fieldTypesLoading) {
    return null;
  }

  const showNoValue = derive.deriveId !== undefined && (value === null || value === undefined);

  return (
    <FlexCol
      gap={0}
      noGrow={fieldType?.fieldDataTypeId === 'yesNo'? true : undefined}
      {...uiField.props}
      data-pdf-field-group="true"
      data-form-field-key={clipboardFieldKey ?? undefined}
    >
      <FieldLabel
        label={label}
        required={uiField.required}
        tooltip={description}
        isProtected={isProtected}
        isDerived={isDerived}
        derivedSentence={derive.sourceSentence}
        derivedComputedAtLabel={derive.computedAtLabel}
        derivedSkipped={derive.skipped}
        derivedNoValue={showNoValue}
      />
      <ControlUI
        value={value}
        uiField={uiField}
        onChange={setValue}
        readOnly={controlDisabled}
        fieldType={fieldType}
        onFocus={handleFocus}
        onBlur={handleBlur}
        status={derive.skipped ? 'warning' : validationStatus}
        style={showNoValue ? noValueControlStyle : controlStyle}
        {...props}
        placeholder={showNoValue ? formDeriveCopy.noValueTitle : props?.placeholder}
        title={computedSubFieldReason ?? props?.title}
        className={
          [
            derive.computing ? 'calculating-value' : undefined,
            recovery.differs ? 'recovery-differing-value' : undefined,
            props?.className,
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
        aria-busy={derive.computing || undefined}
      />
      <FieldErrorTooltip
        errorMessage={errorMessage ?? ''}
        open={!!validationStatus && showPopover && isFocused}
        onDismiss={handleDismissError}
      />
    </FlexCol>
  );
});

FieldUI.displayName = 'FieldUI';

export default FieldUI;